import { observer } from "mobx-react";
import { compact, isEmpty, noop } from "lodash";
import { TFunction, TMessage } from "../../i18n/tt";
import {
  ConfirmSendToBRDialogModel,
  DecisionListItem,
  DialogState,
  getDecisionFollowUpPanels,
  getSigningStatusColor,
  getSigningStatusLabel,
  getViewingStatusColor,
  getViewingStatusLabel,
  RecipientModel,
  RecipientViewModel,
  ReopenLockedDocumentDialogModel,
  ReviewDocumentSigningDialogModel,
  ReviewSendNoticeOfBoardMeetingDialogModel,
  ReviewSendNoticeOfGeneralMeetingDialogModel,
  ReviewSendSubscriptionFormDialogModel,
  SendForReviewDialogModel,
  ViewDocumentSigningStatusDialogModel,
  ViewDocumentViewingStatusDialogModel,
  ViewSubscriptionFormBatchPreviewModel,
  ViewSubscriptionStatusDialogModel,
} from "./summaryDomain";
import {
  IdentificationForPerson,
  ProvideEmailToAttachmentForm,
  ProvideIdentificationToPersonsForm,
} from "./SummaryController";
import { FC, Fragment, ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Alert as AlertV2 } from "../../components-v2/Alert";
import { LoadingIndicator } from "../../components-v2/LoadingIndicator";
import {
  DetailsSummaryList,
  EditDetailsPanel,
  LocalDateTimePicker,
} from "../common/commonComponents";
import {
  getSigningMethodLabel,
  isFailure,
  isOk,
  isPending,
  SigningMethod,
} from "../../common/common";
import {
  AttachmentType,
  BRSignerRole,
  DbotEvent,
  DbotEventDispatcher,
  deleteAttachmentEvent,
  DocumentViewingStatus,
  FollowUp,
  FollowUpDetailsPanel,
  FollowUpStatus,
  FollowUpType,
  getDecisionAttachmentsDescription,
  getDecisionAttachmentsEmailInformation,
  getDecisionAttachmentsEmailTitle,
  getDecisionName,
  getDecisionSendToBRDescription,
  getOpenNextPanelEvent,
  getOpenPrevPanelEvent,
  MeetingAuthMethod,
  ProjectCompany,
  ProjectContext,
  SendToBRFollowUp,
} from "../common/commonDomain";
import { Button, LinkButton } from "../../components-v2/Button";
import { SigningStatusForSigner, SubscriptionStatus } from "../../document/documentApi";
import { RequireSubscriptionButton } from "../projectList/projectListComponents";
import { requiresSubscription } from "../../payment/Payment";
import { Tag } from "../../components-v2/Tag";
import { Dialog } from "../../components-v2/Dialog";
import { MarkdownEditor } from "../../text/MarkdownEditor";
import { Box, Grid, VBox } from "../../components-v2/Box";
import { Tabs } from "../../components-v2/Tabs";
import { Checkbox } from "../../components-v2/Checkbox";
import { getFullName } from "../../utils/Roles";
import { Icon } from "../../components-v2/Icon";
import { formatDate, formatDateTime, relativeTime } from "../../i18n/dateFormat";
import { MTextInput } from "../../components-v2/TextInput";
import { Table } from "../../components-v2/Table";
import { SubscribersStatusDialog } from "../increaseOfShareCapital/SubscribersStatus";
import { MRadioButtonGroup } from "../../components-v2/RadioButton";
import { AttachmentsPanel } from "../../components-v2/AttachmentsPanel";
import {
  adminBoardArchive,
  adminRoles,
  adminSettings,
  adminShareholderArchive,
  adminShareholders,
} from "../../utils/navigation";
import { MSelect } from "../../components-v2/Select";
import { isMobileView } from "../../utils/screen";
import { Markdown } from "../../text/Markdown";
import { when } from "../../utils/functions";
import { DecisionType } from "../../common/decisions";
import {
  useDispatch,
  useLanguage,
  useMessageStore,
  useTranslation,
  useUserStore,
} from "../../stores/RootStoreContext";
import { isProductionEnvironment } from "../../utils/environments";
import { BatchPreviewDialog } from "../../components-v2/BatchPreviewDialog";
import { BatchPreviewReady } from "../../components-v2/BatchPreviewDialog/BatchPreview";
import { ElectronicSubscriptionForm } from "../../views/Pages/SubscriptionFormSign/subscriptionFormComponents";
import {
  confirmAction,
  EventDispatcher,
  navigate,
  showPaymentDialog,
  thunk,
} from "../../events/eventsDomain";
import {
  ConfirmDeleteSigningRequestDialog,
  SendForSigningDialog,
  SendingTime,
} from "../../common/commonComponents";
import { RadioFieldModel, SignerModel, StringFieldModel } from "../../common/commonForms";
import { InfoButton } from "../../components-v2/InfoButton";
import { shareTypeLabel } from "../../common/share";
import { DocumentType } from "../../common/documents";
import { DataTable, TableColumn } from "../../components-v2/DataTable";
import { CompanyRole } from "../../types/CompanyInfo";
import { LocalizedTextEditor } from "../../text/LocalizedTextEditor";
import { ProjectController } from "../project/projectDomain";
import { getActionIcon, getActionLabel } from "../../archive/commonArchiveDomain";
import { getMissingIdentificationDocumentations } from "../ElectionOfCompanyBoard/electionOfCompanyBoardDomain";

export const ReviewSendNoticeOfBoardMeetingDialog = observer(
  function ReviewSendNoticeOfBoardMeeting({
    model,
    dispatch,
  }: {
    model: ReviewSendNoticeOfBoardMeetingDialogModel;
    dispatch: EventDispatcher;
  }) {
    const { t } = useTranslation();
    const userStore = useUserStore();

    useEffect(() => {
      model.init(t);
    }, [model, t]);

    const subscriptionButton = (
      <RequireSubscriptionButton
        subscriptionProps={{
          ...model.subscriptionTypeModel,
          isLoading: model.isSending,
          isDisabled: !model.canSubmit,
          onNavigateToPayment: () => dispatch([showPaymentDialog]),
        }}
      />
    );
    return (
      <Dialog
        title={t(model.title)}
        submitLabel={t("send")}
        isSubmitting={model.isSending}
        isSubmitDisabled={!model.canSubmit}
        isCancelDisabled={!model.canClose}
        onSubmit={model.submit}
        onCancel={model.close}
        actions={requiresSubscription(model.subscriptionTypeModel) ? subscriptionButton : null}
      >
        <VBox gap={"lg"}>
          <LocalizedTextEditor model={model.emailBody} />
          {model.showAccessTypeAndRecipients && (
            <>
              <p>{t("dbot.meeting.email-link")}</p>
              <MeetingAuthenticationRadioGroup
                authMethod={model.authMethod}
                meetingPassword={model.meetingPassword}
                loginAlert={renderBoardMeetingLoginAlert(model, t, () =>
                  dispatch([navigate(adminRoles(userStore.orgNumber))]),
                )}
              />
              <VBox>
                <div>{t("dbot.recipients")}</div>
                <Grid cols={2} gap={"xs"}>
                  <div>{t("name")}</div>
                  <div>{t("email")}</div>
                  {model.recipients.recipientFields.map((item) => (
                    <Fragment key={item.id}>
                      <MTextInput model={item.nameField} disabled />
                      <MTextInput model={item.emailField} placeholder={t("email")} />
                    </Fragment>
                  ))}
                </Grid>
                {model.recipients.error && (
                  <Alert type={"error"} message={t(model.recipients.error)} />
                )}
              </VBox>
            </>
          )}
        </VBox>
      </Dialog>
    );
  },
);

const MeetingAuthenticationRadioGroup = observer(function MeetingAuthenticationRadioGroup({
  authMethod,
  meetingPassword,
  loginAlert,
}: {
  authMethod: RadioFieldModel<MeetingAuthMethod | null>;
  meetingPassword: StringFieldModel;
  loginAlert?: ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <>
      <MRadioButtonGroup
        labelFn={(option) => {
          switch (option) {
            case null:
              return t("dbot.meeting.access-open");
            case "LOGIN":
              return t("dbot.meeting.access-bankid");
            default:
              return t("dbot.meeting.access-password");
          }
        }}
        model={authMethod}
        horizontal={!isMobileView()}
      />
      {authMethod.value === null && <Alert message={t("dbot.meeting.direct-link")} type={"info"} />}
      {authMethod.value === "PASSWORD" && (
        <Grid cols={2} gap={"xs"}>
          <MTextInput model={meetingPassword} label={t("dbot.meeting.password-label")} />
          <Box alignItems={"end"} padding={"md"}>
            <InfoButton infoText={t("dbot.meeting.direct-link-password.info")} />
          </Box>
        </Grid>
      )}
      {authMethod.value === "LOGIN" && loginAlert}
    </>
  );
});

function renderBoardMeetingLoginAlert(
  model: ReviewSendNoticeOfBoardMeetingDialogModel,
  t: TFunction,
  openCompanyRoles: () => void,
) {
  switch (true) {
    case model.hasExtraParticipants:
      return (
        <Alert
          message={<Markdown value={t("dbot.meeting.login.extra-participants")} />}
          type={"error"}
          icon={"warning"}
        />
      );
    case !model.hasAllSocialSecurityNumbers:
      return (
        <Alert
          icon={"warning"}
          message={
            <VBox alignItems={"start"}>
              <Markdown value={t("dbot.meeting.login.missing-ssn")} />
              <LinkButton
                label={t("dbot.meeting.open-company-roles")}
                onClick={openCompanyRoles}
                color={"inherit"}
              />
            </VBox>
          }
          type={"error"}
        />
      );
    default:
      return (
        <Alert
          icon={"info"}
          message={<Markdown value={t("dbot.meeting.login.info")} />}
          type={"info"}
        />
      );
  }
}

function renderGeneralMeetingLoginAlert(
  model: ReviewSendNoticeOfGeneralMeetingDialogModel,
  t: TFunction,
  openShareholders: () => void,
) {
  return (
    <>
      {model.params.isMissingShareholderSSN ? (
        <Alert
          icon={"warning"}
          message={
            <VBox alignItems={"start"}>
              <Markdown value={t("dbot.meeting.login.missing-ssn")} />
              <LinkButton
                label={t("dbot.meeting.open-shareholders")}
                onClick={openShareholders}
                color={"inherit"}
              />
            </VBox>
          }
          type={"error"}
        />
      ) : (
        <Alert
          icon={"info"}
          message={<Markdown value={t("dbot.meeting.login.info")} />}
          type={"info"}
        />
      )}
      {model.params.hasForeignShareholders && (
        <Alert
          message={<Markdown value={t("dbot.meeting.login.foreign-shareholders")} />}
          type={"warning"}
        />
      )}
    </>
  );
}

export const ReviewSendNoticeOfGeneralMeetingDialog = observer(
  ({
    model,
    dispatch,
  }: {
    model: ReviewSendNoticeOfGeneralMeetingDialogModel;
    dispatch: EventDispatcher;
  }) => {
    const { t } = useTranslation();
    const userStore = useUserStore();

    useEffect(() => {
      model.init(t);
    }, [model, t]);

    const subscriptionButton = (
      <RequireSubscriptionButton
        subscriptionProps={{
          ...model.subscriptionTypeModel,
          isLoading: model.isSending,
          isDisabled: !model.canSubmit,
          onNavigateToPayment: () => {
            dispatch([showPaymentDialog]);
          },
        }}
      />
    );
    return (
      <Dialog
        title={t(model.title)}
        submitLabel={t("send")}
        isSubmitting={model.isSending}
        isSubmitDisabled={!model.canSubmit}
        isCancelDisabled={!model.canClose}
        onSubmit={model.submit}
        onCancel={model.close}
        actions={requiresSubscription(model.subscriptionTypeModel) ? subscriptionButton : null}
      >
        <VBox>
          {model.hasInvalidCompanyRoleRecipients ? (
            <Alert
              type={"error"}
              message={t("dbot.summary.noticeOfGeneralMeeting.hasInvalidRoles")}
            />
          ) : (
            <VBox gap={"lg"}>
              <LocalizedTextEditor model={model.emailBody} />
              <VBox>
                <p>{t("dbot.meeting.email-link")}</p>
                <MeetingAuthenticationRadioGroup
                  authMethod={model.authMethod}
                  meetingPassword={model.meetingPassword}
                  loginAlert={renderGeneralMeetingLoginAlert(model, t, () =>
                    dispatch([navigate(adminShareholders(userStore.orgNumber))]),
                  )}
                />
                <div>{t("dbot.recipients")}</div>
                <Grid cols={2} gap={"xs"}>
                  <div>{t("name")}</div>
                  <div>{t("email")}</div>
                  {model.recipients.recipientFields.map((item) => (
                    <Fragment key={item.id}>
                      <MTextInput model={item.nameField} disabled />
                      <MTextInput
                        model={item.emailField}
                        placeholder={t("email")}
                        onBlur={() => {
                          item.emailField.commit();
                          model.updateShareholderEmail(item);
                        }}
                      />
                    </Fragment>
                  ))}
                </Grid>
                {model.recipients.error && (
                  <Alert type={"error"} message={t(model.recipients.error)} />
                )}
                {isOk(model.updateEmailResult) && (
                  <Alert type={"success"} message={<>{t(model.updateEmailResult.data)}</>} />
                )}
                {isFailure(model.updateEmailResult) && (
                  <Alert type={"error"} message={<>{t(model.updateEmailResult.error)}</>} />
                )}
              </VBox>
            </VBox>
          )}
        </VBox>
      </Dialog>
    );
  },
);

export const ReviewSendSubscriptionFormDialog = observer(
  ({
    model,
    dispatch,
  }: {
    model: ReviewSendSubscriptionFormDialogModel;
    dispatch: EventDispatcher;
  }) => {
    const { t } = useTranslation();
    const locale = useLanguage();
    const subscriptionButton = (
      <RequireSubscriptionButton
        subscriptionProps={{
          ...model.subscriptionTypeModel,
          isLoading: model.isSending,
          isDisabled: !model.canSubmit,
          onNavigateToPayment: () => dispatch([showPaymentDialog]),
        }}
      />
    );
    return (
      <SendForSigningDialog
        form={model.form}
        title={model.title}
        isSending={model.isSending}
        canSubmit={model.canSubmit}
        canClose={model.canClose}
        close={model.close}
        submit={model.submit}
        submitButton={requiresSubscription(model.subscriptionTypeModel) ? subscriptionButton : null}
        information={
          model.form.isSignerListEmpty
            ? {
                message: t("dbot.summary.subscriptionForm.increaseNominalValueNotSupported"),
              }
            : undefined
        }
        commonSigningProperties={model.form.commonSigningProperties(locale)}
      />
    );
  },
);

export const SendForReviewDialog = observer(function SendForReviewDialog({
  model,
}: {
  model: SendForReviewDialogModel;
}) {
  const { t } = useTranslation();
  const scrollRef = useRef<HTMLDivElement>(null);
  const locale = useLanguage();

  useEffect(() => {
    model.init(t);
  }, [model, t]);

  useEffect(() => {
    if (scrollRef !== null && model.extraRecipients.length > 0) {
      scrollRef.current?.lastElementChild?.scrollIntoView();
    }
  }, [model.extraRecipients]);

  const columns: TableColumn<CompanyRole | RecipientModel>[] = compact([
    {
      name: "name",
      label: t("dbot.recipients"),
      align: "left",
      getValue: (recipient) =>
        model.isCompanyRole(recipient) ? (
          <Box>
            <Checkbox
              id={`message-recipient-${recipient.companyroleId}`}
              name={"message-recipient"}
              checked={!model.excludedRecipients.has(recipient.companyroleId)}
              onChange={noop} // handled by onRowClick in the DataTable below
            />
            <div>{getFullName(recipient)}</div>
          </Box>
        ) : (
          <MTextInput
            model={recipient.name}
            autofocus
            placeholder={t("name")}
            messageVariant="tooltip"
          />
        ),
      getSortValue: (row) => model.isCompanyRole(row) && getFullName(row),
    },
    {
      name: "role",
      label: t("roles"),
      align: "left",
      getValue: (recipient) =>
        model.isCompanyRole(recipient) ? (
          <div>{when(model.getRoleLabel(recipient), (label) => t(label))}</div>
        ) : (
          <div>{t("roles.external_recipient")}</div>
        ),
      getSortValue: (row) => model.isCompanyRole(row) && row.role,
    },
    {
      name: "email",
      label: t("email"),
      align: "left",
      getValue: (recipient) =>
        model.isCompanyRole(recipient) ? (
          <div>{recipient.email}</div>
        ) : (
          <MTextInput model={recipient.email} placeholder={t("email")} messageVariant="tooltip" />
        ),
      getSortValue: (row) => model.isCompanyRole(row) && row.email,
    },
  ]);

  return (
    <Dialog
      title={t("dbot.send-for-review")}
      onCancel={model.cancel}
      onSubmit={() => model.submit(locale)}
      submitLabel={t("send")}
      isSubmitting={model.isSending}
      actions={
        <Box
          gap={"md"}
          justifyContent={"space-between"}
          alignItems={"center"}
          vertical={isMobileView()}
        >
          {model.view === "recipients" ? (
            <LinkButton
              label={t("documents.noticeBoardMeeting.addRecipient")}
              icon={"plus"}
              onClick={model.addRecipient}
            />
          ) : (
            <span />
          )}
          <Box>
            <Button
              label={t("cancel")}
              onClick={model.cancel}
              disabled={model.isSending}
              variant="secondary"
            />
            <Button
              variant={"primary"}
              label={t("send")}
              onClick={() => model.submit(locale)}
              loading={model.isSending}
              disabled={model.canSubmit}
            />
          </Box>
        </Box>
      }
    >
      <VBox>
        <Tabs
          scrollable={false}
          tabs={model.viewOptions.map((v) => ({
            id: v,
            label: t(model.viewLabel(v)),
            isActive: v === model.view,
            onSelect: () => model.setView(v),
          }))}
        />
        {model.error && <Alert message={t(model.error)} type={"error"} />}
        {model.view === "options" && (
          <VBox marginX marginTop gap={"md"}>
            <Box alignItems={"center"}>
              <Checkbox
                label={t(model.votingOnDecisionMatterLabel)}
                checked={model.enabledVotingOnDecisionMatters}
                onChange={model.toggleEnableVotingOnDecisionMatter}
                disabled={model.disableVotingOnMatter}
              />
              <InfoButton infoText={<Markdown value={t(model.infoButtonMessage())} />} />
            </Box>
            <Box alignItems="center">
              <Checkbox
                label={t("set-deadline")}
                checked={model.deadlineForFeedback}
                onChange={model.toggleDeadlineForFeedback}
              />
              {model.deadlineForFeedback && (
                <LocalDateTimePicker
                  dateField={model.deadlineDate}
                  timeField={model.deadlineTime}
                  onDateChange={() => model.updateDeadlineFeedbackDate(t)}
                  onTimeFieldBlur={() => model.updateDeadlineFeedbackDate(t)}
                  messageVariant="tooltip"
                />
              )}
            </Box>
            <div className={"tw-h-[25rem]"}>
              <MarkdownEditor
                label={t("customize-message")}
                size={"full"}
                idleTimeout={20000}
                value={model.body}
                onChange={(value) => model.setBody(value, t)}
              />
            </div>
          </VBox>
        )}
        {model.view === "recipients" && (
          <VBox paddingRight={"md"}>
            <Box marginTop={"sm"} marginBottom={"sm"}>
              <LinkButton
                label={model.isAllSelected ? t("select-none") : t("select-all")}
                onClick={model.toggleAllRecipients}
              />
            </Box>
            <DataTable
              containerClassName={"tw-max-h-[27rem]"}
              hoverable
              tableColumns={columns}
              onRowClick={(row) => {
                if (model.isCompanyRole(row)) {
                  model.toggleRecipient(row.companyroleId);
                }
              }}
              tableData={[...model.recipients, ...model.extraRecipients]}
              getId={(row) => model.getId(row)}
              rowActions={(row) =>
                compact([
                  model.canDeleteRecipient(row) && [
                    "icon",
                    {
                      icon: "trash",
                      tooltip: { key: "delete" },
                      onClick: () => model.deleteRecipient(row),
                      color: "error",
                    },
                  ],
                ])
              }
            />
            {model.extraRecipients.length > 0 && (
              <>
                <Alert type="info" message={t("only-board-members-can-vote")} />
                <div ref={scrollRef} />
              </>
            )}
          </VBox>
        )}
      </VBox>
    </Dialog>
  );
});

export const ReviewDocumentSigningDialog = observer(function ReviewDocumentSigning({
  model,
  dispatch,
}: {
  model: ReviewDocumentSigningDialogModel;
  dispatch: EventDispatcher;
}) {
  const { t } = useTranslation();
  const locale = useLanguage();
  const subscriptionButton = (
    <RequireSubscriptionButton
      subscriptionProps={{
        ...model.subscriptionTypeModel,
        isLoading: model.isSending,
        isDisabled: !model.canSubmit,
        onNavigateToPayment: () => dispatch([showPaymentDialog]),
      }}
    />
  );

  return (
    <SendForSigningDialog
      form={model.form}
      title={model.title}
      isSending={model.isSending}
      canSubmit={model.canSubmit}
      canClose={model.canClose}
      close={model.close}
      submit={model.submit}
      submitButton={requiresSubscription(model.subscriptionTypeModel) ? subscriptionButton : null}
      information={
        model.infoMessage
          ? { message: t(model.infoMessage) }
          : model.form.isSubscriptionSignersPresent
            ? {
                icon: "certificate",
                message: (
                  <span>
                    <strong>{t("signing.isSubscriptionSignersPresent")}</strong>{" "}
                    {t("signing.isSubscriptionSignersPresent.description")}
                  </span>
                ),
              }
            : undefined
      }
      showUnsupportedAttachmentWarning={model.showUnsupportedAttachmentWarning}
      destinationFolderLabel={
        model.params.document.type === DocumentType.board_meeting
          ? "archive.board-archive"
          : model.params.document.type === DocumentType.general_meeting
            ? "archive.shareholder-archive"
            : undefined
      }
      signingCompleteInfoMessage={
        model.params.document.type === DocumentType.board_meeting ? (
          <div>
            {t("send-for-signing.board-meeting-minutes.saved-to-archive.message-start")}
            <a
              rel="noopener noreferrer"
              target={"_blank"}
              href={adminBoardArchive(model.params.orgNo)}
              className={"text-inherit"}
            >
              {t("send-for-signing.board-meeting-minutes.saved-to-archive.link")}
            </a>
            {t("send-for-signing.board-meeting-minutes.saved-to-archive.message-end")}
          </div>
        ) : model.params.document.type === DocumentType.general_meeting ? (
          <div>
            {t("send-for-signing.general-meeting-minutes.saved-to-archive.message-start")}
            <a
              rel="noopener noreferrer"
              target={"_blank"}
              href={adminShareholderArchive(model.params.orgNo)}
              className={"text-inherit"}
            >
              {t("send-for-signing.general-meeting-minutes.saved-to-archive.link")}
            </a>
            {t("send-for-signing.general-meeting-minutes.saved-to-archive.message-end")}
          </div>
        ) : undefined
      }
      showMessageFields
      commonSigningProperties={model.form.commonSigningProperties(locale)}
      enableFullscreenMode
      documentsForSigning={model.documentsForSigning}
      companiesForSigning={model.companiesForSigning}
      preview={model.documentPreview({ t })}
    />
  );
});

const SigningStatusTableEditRow: FC<{
  model: ViewDocumentSigningStatusDialogModel;
  signer: SignerModel;
}> = observer(({ model, signer }) => {
  const { t } = useTranslation();

  return (
    <tr>
      <td>{signer.nameField.inputValue}</td>
      <td>
        <MTextInput model={signer.emailField} placeholder={t("email")} variant={"in-table"} />
      </td>
      <td>
        <MSelect name={"language"} model={signer.languageField} />
      </td>
      <td colSpan={2}>
        <MSelect name={"signingMethod"} model={signer.methodField} />
      </td>
      <td>
        <Box gap={"md"}>
          {model.showCancelEditButton() && (
            <LinkButton label={t("cancel")} onClick={model.cancelEdit} />
          )}
          <LinkButton
            icon={"paper-plane"}
            label={t("send")}
            loading={model.isSendingEditedSigner()}
            disabled={model.isSendingEditedSigner()}
            onClick={model.sendEditedSigner}
          />
        </Box>
      </td>
    </tr>
  );
});

const SigningStatusTableRow: FC<{
  model: ViewDocumentSigningStatusDialogModel;
  signer: SigningStatusForSigner;
}> = observer(({ model, signer }) => {
  const { t } = useTranslation();
  return (
    <tr>
      <td>
        <VBox gap={false}>
          <div>{signer.name}</div>
          <SendingTime sendingTime={signer.sendingTime} />
        </VBox>
      </td>
      <td>{signer.email}</td>
      <td>{signer.language ? t(`language.${signer.language}`) : ""}</td>
      <td>{t(getSigningMethodLabel(signer.signingMethod))}</td>
      <td>
        <Tag color={getSigningStatusColor(signer)} label={t(getSigningStatusLabel(signer))} />
      </td>
      <td>
        <Box gap={"md"} justifyContent={"end"}>
          {model.showEditButton(signer) && (
            <LinkButton
              label={t("edit")}
              disabled={model.isEditDisabled()}
              onClick={() => model.edit(signer)}
            />
          )}
          {model.showResendButton(signer) && (
            <LinkButton
              label={t("dbot.summary.resend")}
              loading={model.isResending(signer)}
              disabled={model.isResendDisabled(signer)}
              onClick={() => model.resend(signer)}
            />
          )}
          {model.showCopyLinkButton && signer.signingUrl && (
            <LinkButton
              icon={"link"}
              label={t("signing.link")}
              onClick={() => model.copySigningLinkToClipboard(signer)}
              tooltip={t(model.tooltipLabel(signer))}
              disabled={signer.signingMethod === SigningMethod.HANDWRITING}
            />
          )}
        </Box>
      </td>
    </tr>
  );
});

const SigningStatusTable: FC<{
  model: ViewDocumentSigningStatusDialogModel;
}> = observer(({ model }) => {
  const { t } = useTranslation();
  return (
    <Table variant={"IN_CARD_DENSE"} withRowBorders>
      {model.groupedSigners.map(([companyName, signers], i) => (
        <Fragment key={companyName}>
          <thead>
            {model.showGroupHeading(companyName) && (
              <tr>
                <th align={"left"}>
                  <Box
                    fontWeight={"bold"}
                    textSize={"normal"}
                    fg={"default"}
                    marginTop={i > 0}
                    marginBottom={"sm"}
                  >
                    {companyName}
                  </Box>
                </th>
              </tr>
            )}
            <tr>
              <th align={"left"}>{t("name")}</th>
              <th align={"left"}>{t("email")}</th>
              <th align={"left"}>{t("documents.send.recipientEmailLanguage")}</th>
              <th align={"left"}>{t("signing.signingMethod")}</th>
              <th align={"left"}>{t("dbot.summary.status")}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {signers.map((s) =>
              model.state.type === "edit" && model.state.signedDocumentId === s.signedDocumentId ? (
                <SigningStatusTableEditRow
                  key={s.signedDocumentId}
                  model={model}
                  signer={model.state.model}
                />
              ) : (
                <SigningStatusTableRow key={s.signedDocumentId} model={model} signer={s} />
              ),
            )}
          </tbody>
        </Fragment>
      ))}
    </Table>
  );
});

export const ViewDocumentSigningStatusDialog = observer(
  ({ model }: { model: ViewDocumentSigningStatusDialogModel }) => {
    const { t } = useTranslation();

    function renderContent() {
      switch (model.documentSigningStatus.state) {
        case "pending":
          return <LoadingIndicator />;
        case "failure":
          return <Alert type={"error"} message={t(model.documentSigningStatus.error)} />;
        case "ok":
          return (
            <VBox>
              {model.alertMessage && (
                <Alert
                  type={model.alertMessage.type}
                  message={t(model.alertMessage.message)}
                  onClose={model.clearAlertMessage}
                />
              )}
              <SigningStatusTable model={model} />
            </VBox>
          );
      }
    }

    return (
      <Dialog
        title={t(model.title)}
        maxWidth={"lg"}
        cancelLabel={t("close")}
        onCancel={model.showCloseButton ? model.close : undefined}
        isCancelDisabled={model.isCloseDisabled}
        actions={
          <Box
            justifyContent={
              model.confirmCancelSignatureRequest && model.hideCancelSignatureRequestButton
                ? "space-between"
                : "end"
            }
          >
            {model.confirmCancelSignatureRequest && model.hideCancelSignatureRequestButton && (
              <LinkButton
                label={getActionLabel("delete-signing-request")}
                icon={getActionIcon("delete-signing-request")}
                onClick={model.openConfirmDeleteSigningRequestDialog}
              />
            )}
            <Box>
              <Button
                label={t("close")}
                variant={"secondary"}
                onClick={model.showCloseButton ? model.close : undefined}
                disabled={model.isCloseDisabled}
              />
              <Button
                variant={"primary"}
                disabled={model.isSubmitDisabled}
                label={when(model.submitLabel, t)}
                loading={model.isSubmitting}
                onClick={model.showSubmitButton ? model.submit : undefined}
              />
            </Box>
          </Box>
        }
      >
        <>
          {renderContent()}
          {model.state.type === "delete-signature-request" &&
            model.confirmCancelSignatureRequest && (
              <ConfirmDeleteSigningRequestDialog
                isSubmitting={model.state.isLoading}
                onConfirm={model.confirmCancelSignatureRequest}
                onCancel={model.onClose}
              />
            )}
        </>
      </Dialog>
    );
  },
);

const SubscriptionStatusTableRow: FC<{
  subscription: SubscriptionStatus;
}> = observer(({ subscription }) => {
  const { t } = useTranslation();
  const locale = useLanguage();
  return (
    <tr>
      <td align={"left"}>{subscription.name}</td>
      <td align={"left"}>{t(shareTypeLabel(subscription.shareTypeCode))}</td>
      <td align={"right"}>{subscription.availableShares}</td>
      <td align={"right"}>{subscription.isSigned ? subscription.numberOfShares : "-"}</td>
      <td align={"right"}>
        {subscription.isSigned ? formatDateTime(locale, subscription.signingTime) : ""}
      </td>
    </tr>
  );
});

const SubscriptionStatusTable: FC<{
  model: ViewSubscriptionStatusDialogModel;
}> = observer(({ model }) => {
  const { t } = useTranslation();
  return (
    <Table variant={"IN_CARD_DENSE"} withRowBorders>
      <thead>
        <tr>
          <th align={"left"}>{t("name")}</th>
          <th align={"left"}>{t("dbot.summary.subscription.shareType")}</th>
          <th align={"right"}>{t("dbot.summary.subscription.availableShares")}</th>
          <th align={"right"}>{t("dbot.summary.subscription.subscribed")}</th>
          <th align={"right"}>{t("dbot.summary.subscription.signatureDate")}</th>
        </tr>
      </thead>
      <tbody>
        {model.subscriptions.map((s) => (
          <SubscriptionStatusTableRow key={`${s.name}_${s.shareTypeId}`} subscription={s} />
        ))}
      </tbody>
    </Table>
  );
});

export const ViewSubscriptionStatusDialog = observer(
  ({ model }: { model: ViewSubscriptionStatusDialogModel }) => {
    const { t } = useTranslation();

    function renderContent() {
      switch (model.subscriptionStatus.state) {
        case "pending":
          return <LoadingIndicator />;
        case "failure":
          return <Alert type={"error"} message={t(model.subscriptionStatus.error)} />;
        case "ok":
          return <SubscriptionStatusTable model={model} />;
      }
    }

    return (
      <Dialog
        title={t("dbot.summary.subscription")}
        cancelLabel={t("close")}
        submitLabel={when(model.submitLabel, t)}
        isSubmitting={model.isSubmitting}
        isSubmitDisabled={model.isSubmitDisabled}
        onSubmit={model.showSubmitButton ? model.submit : undefined}
        onCancel={model.showCloseButton ? model.close : undefined}
        maxWidth={"lg"}
        isCancelDisabled={model.isCloseDisabled}
      >
        {renderContent()}
      </Dialog>
    );
  },
);

const RecipientStatusTableEditRow: FC<{
  model: ViewDocumentViewingStatusDialogModel;
  recipient: RecipientViewModel;
}> = observer(({ model, recipient }) => {
  const { t } = useTranslation();
  return (
    <tr>
      <td>{recipient.name}</td>
      <td>
        <MTextInput model={recipient.emailField} placeholder={t("email")} />
      </td>
      <td></td>
      <td>
        <Box gap={"md"}>
          {model.showCancelEditButton() && (
            <LinkButton label={t("cancel")} onClick={model.cancelEdit} />
          )}
          <LinkButton
            label={t("send")}
            icon={"paper-plane"}
            loading={model.isSendingEditedRecipient()}
            disabled={model.isSendingEditedRecipient()}
            onClick={() => {
              model.sendUpdatedRecipient();
            }}
          />
        </Box>
      </td>
    </tr>
  );
});

const RecipientStatusTableViewRow: FC<{
  model: ViewDocumentViewingStatusDialogModel;
  recipient: DocumentViewingStatus;
}> = observer(({ model, recipient }) => {
  const { t } = useTranslation();

  return (
    <tr>
      <td>
        <VBox gap={false}>
          <div>{recipient.recipientName}</div>
          <SendingTime sendingTime={recipient.sendingTime} />
        </VBox>
      </td>
      <td>{recipient.recipientEmail}</td>
      <td>
        <Tag color={getViewingStatusColor(recipient)} label={t(getViewingStatusLabel(recipient))} />
      </td>
      <td>
        <Box gap={"md"}>
          <LinkButton
            label={t("edit")}
            disabled={model.isEditDisabled()}
            onClick={() => model.edit(recipient)}
          />
          <LinkButton
            label={t("dbot.summary.resend")}
            disabled={model.isResendDisabled()}
            loading={model.isResending(recipient)}
            onClick={() => model.resend(recipient)}
          />
        </Box>
      </td>
    </tr>
  );
});

const ViewDocumentSentStatusTable: FC<{
  model: ViewDocumentViewingStatusDialogModel;
}> = observer(({ model }) => {
  const { t } = useTranslation();
  return (
    <Table variant={"IN_CARD_DENSE"} withRowBorders>
      <thead>
        <tr>
          <th align={"left"}>{t("name")}</th>
          <th align={"left"}>{t("email")}</th>
          <th align={"left"}>{t("dbot.summary.status")}</th>
          <th />
        </tr>
      </thead>
      <tbody>
        {model.recipientView.map((r) =>
          model.state.type === "edit" && model.state.viewDocumentId === r.recipientDocumentId ? (
            <RecipientStatusTableEditRow
              key={r.recipientDocumentId}
              model={model}
              recipient={model.state.model}
            />
          ) : (
            <RecipientStatusTableViewRow key={r.recipientDocumentId} model={model} recipient={r} />
          ),
        )}
      </tbody>
    </Table>
  );
});

export const ViewDocumentSentStatusDialog = observer(
  ({ model }: { model: ViewDocumentViewingStatusDialogModel }) => {
    const { t } = useTranslation();

    function renderContent() {
      switch (model.status.state) {
        case "pending":
          return <LoadingIndicator />;
        case "failure":
          return <Alert type={"error"} message={t(model.status.error)} />;
        case "ok":
          return (
            <VBox>
              {model.alertMessage && (
                <Alert
                  type={model.alertMessage.type}
                  message={t(model.alertMessage.message)}
                  onClose={model.clearAlertMessage}
                />
              )}
              <div className={"text-lg"}>{t("documents.document-are-sent-to")}</div>
              <ViewDocumentSentStatusTable model={model} />
            </VBox>
          );
      }
    }

    return (
      <Dialog
        title={t(model.title)}
        isSubmitting={model.isReloading}
        onCancel={model.close}
        actions={
          <Box justifyContent={"space-between"}>
            <LinkButton
              label={t("dbot.viewing.refresh")}
              icon={"arrows-clockwise"}
              onClick={model.showReloadButton ? model.reload : undefined}
              loading={model.isReloading}
            />
            <Button variant={"secondary"} label={t("close")} onClick={model.close} />
          </Box>
        }
      >
        {renderContent()}
      </Dialog>
    );
  },
);

function getMembers(item: DecisionListItem): IdentificationPopupInput {
  switch (item.decision.decisionData?.type) {
    case DecisionType.assignsNewCompanyBoard:
      return {
        items: compact([
          ...item.decision.decisionData.membersByName.map((member) => member.name),
          ...(item.decision.decisionData.deputyByName?.map((member) => member.name) ?? []),
        ]).map((d) => ({ name: d, identityNumber: "", email: "" })),
        title: { key: "dbot.requiredAttachments.BOARD_MEMBERS_ELECTION_IDENTIFICATIONS.title" },
        description: {
          key: "dbot.requiredAttachments.BOARD_MEMBERS_ELECTION_IDENTIFICATIONS.description",
        },
      };
    case DecisionType.employmentOfManagingDirector:
      return {
        items: Array.of({
          name: item.decision.decisionData.newManagingDirector ?? "",
          identityNumber: "",
          email: "",
        }),
        title: { key: "dbot.requiredAttachments.EMPLOY_CEO_IDENTIFICATION.title" },
        description: { key: "dbot.requiredAttachments.EMPLOY_CEO_IDENTIFICATION.description" },
      };
    case DecisionType.procura:
      return {
        items: item.decision.decisionData.procuraAssignedToByName
          .flatMap((d) => d.others)
          .map((d) => ({ name: d, identityNumber: "", email: "" })),
        title: { key: "dbot.requiredAttachments.PROCURA_BY_NAME_OTHERS_IDENTIFICATION.title" },
        description: {
          key: "dbot.requiredAttachments.PROCURA_BY_NAME_OTHERS_IDENTIFICATION.description",
        },
      };
    case DecisionType.electionOfCompanyBoard:
      return {
        items: getMissingIdentificationDocumentations(item.decision.decisionData.boardChanges),
        title: { key: "dbot.requiredAttachments.BOARD_MEMBERS_ELECTION_IDENTIFICATIONS.title" },
        description: {
          key: "dbot.requiredAttachments.BOARD_MEMBERS_ELECTION_IDENTIFICATIONS.description",
        },
      };
    default:
      return { items: [], title: { translated: "" }, description: { translated: "" } };
  }
}

type IdentificationPopupInput = {
  title: TMessage;
  description: TMessage;
  items: IdentificationForPerson[];
};

const ReopenLockedDocumentDialog: FC<{
  model: ReopenLockedDocumentDialogModel;
}> = observer(({ model }) => {
  const { t } = useTranslation();
  return (
    <Dialog
      title={t({ key: "reopen.dialog.header" })}
      submitLabel={t("reopen.dialog.submitLabel")}
      isSubmitting={model.isLoading()}
      onSubmit={model.onSubmit}
      onCancel={model.onCancel}
    >
      {isFailure(model.reopenResult) && (
        <AlertV2 type="error" message={t("dbot.action.reopen.error")} />
      )}
      <p>{t({ key: "reopen.dialog.description.1" })}</p>
      <p>{t({ key: "reopen.dialog.description.2" })}</p>
    </Dialog>
  );
});

export const SummaryDocumentDialog = observer(
  ({ state }: { state: DialogState; closeDialog: () => void; ctrl: ProjectController }) => {
    const dispatch = useDispatch();
    switch (state.type) {
      case "reviewSendNoticeOfGeneralMeeting":
        return <ReviewSendNoticeOfGeneralMeetingDialog model={state.model} dispatch={dispatch} />;
      case "reviewSendNoticeOfBoardMeeting":
        return <ReviewSendNoticeOfBoardMeetingDialog model={state.model} dispatch={dispatch} />;
      case "reviewDocumentSigning":
        return <ReviewDocumentSigningDialog model={state.model} dispatch={dispatch} />;
      case "reviewSendSubscriptionForm":
        return <ReviewSendSubscriptionFormDialog model={state.model} dispatch={dispatch} />;
      case "viewDocumentSigningStatus":
        return <ViewDocumentSigningStatusDialog model={state.model} />;
      case "viewSubscriptionStatus":
        return <ViewSubscriptionStatusDialog model={state.model} />;
      case "viewIncreaseOfShareCapitalSubscribersStatus":
        return <SubscribersStatusDialog model={state.model} />;
      case "viewIncreaseOfShareCapitalSubscribersAllocation":
        return <SubscribersStatusDialog model={state.model} />;
      case "viewDocumentViewingStatus":
        return <ViewDocumentSentStatusDialog model={state.model} />;
      case "reopenLockedDocument":
        return <ReopenLockedDocumentDialog model={state.model} />;
      case "sendForReview":
        return <SendForReviewDialog model={state.model} />;
      case "confirmSendToBR":
        return <ConfirmSendToBRDialog model={state.model} />;
      case "viewSubscriptionFormBatchPreview":
        return (
          <BatchPreviewDialog
            model={state.model.documentPreviewModel}
            onClose={state.model.close}
            customBody={<SubscriptionFormBatchPreview model={state.model} />}
          />
        );
      default:
        return null;
    }
  },
);

export const SubscriptionFormBatchPreview = observer(function SummaryValidationMessages({
  model,
}: {
  model: ViewSubscriptionFormBatchPreviewModel;
}) {
  return (
    <VBox>
      {model.subscriptions.size > 0 && (
        <VBox paddingTop={"md"} maxWidth={"lg"} container>
          <Tabs
            tabs={[
              {
                id: "document",
                label: { key: "document" },
                isActive: "document" === model.selectedTab,
                onSelect: () => model.setSelectedTab("document"),
              },
              {
                id: "electronicForm",
                label: { key: "electronic-form" },
                isActive: "electronicForm" === model.selectedTab,
                onSelect: () => model.setSelectedTab("electronicForm"),
              },
            ]}
            scrollable={false}
          />
        </VBox>
      )}
      {model.isDocumentTab && <BatchPreviewReady file={model.documentPreviewModel.file} />}
      {model.isElectronicFormTab && (
        <VBox paddingTop={"md"} maxWidth={"md"} container>
          <ElectronicSubscriptionForm
            companyName={model.company.name}
            organisationNumber={model.company.organizationnumber}
            subscription={model.selectedSubscription}
          />
        </VBox>
      )}
    </VBox>
  );
});

export const SummaryValidationMessages = observer(function SummaryValidationMessages({
  decisions,
  validationError,
}: {
  decisions: {
    decisionId: string;
    title: TMessage;
    hasValidationError?: boolean;
  }[];
  validationError?: TMessage[];
}) {
  const { t } = useTranslation();
  const invalidDecisions = decisions.filter((d) => d.hasValidationError);
  if (isEmpty(invalidDecisions) && decisions.length > 0) {
    return null;
  }
  return (
    <>
      {validationError &&
        decisions.length === 0 &&
        validationError.map((error) => <Alert key={t(error)} message={t(error)} type={"error"} />)}
      {decisions.length > 0 && (
        <Alert
          type={"error"}
          message={
            <ul>
              {invalidDecisions.map((d) => (
                <li key={d.decisionId}>
                  {t("dbot.status-and-next-step.error", {
                    decisionType: t(d.title),
                  })}
                </li>
              ))}
            </ul>
          }
        />
      )}
    </>
  );
});

export const ConfirmSendToBRDialog: FC<{
  model: ConfirmSendToBRDialogModel;
}> = observer(({ model }) => {
  const { t } = useTranslation();
  return (
    <Dialog
      title={t("dbot.summary.sendToBR")}
      onCancel={model.onCancel}
      onSubmit={() => model.validateAndSubmit()}
      maxWidth="sm"
      submitLabel={t("dbot.summary.sendToBR.button")}
      submitResult={model.result}
      isSubmitting={isPending(model.result)}
    >
      <VBox gap>
        {isFailure(model.result) && <Alert message={t("general-error")} type={"error"} />}
        <div>{t("dbot.summary.sendToBR.signer")}</div>
        <MRadioButtonGroup
          model={model.brSignerModel}
          disabled={isOk(model.result)}
          labelFn={(option) => {
            return option?.role === BRSignerRole.other
              ? t("sendToBr.specify")
              : `${option?.name} (${
                  option?.role === BRSignerRole.ceo ? t("roles.ceo") : t("roles.chairperson")
                })`;
          }}
        />
        {model.showName && (
          <MTextInput
            model={model.nameModel}
            autofocus
            label={t("name")}
            disabled={isOk(model.result)}
          />
        )}
        {model.showEmailField && (
          <MTextInput
            model={model.emailModel}
            autofocus={!model.showName}
            label={t("email")}
            disabled={isOk(model.result)}
          />
        )}
      </VBox>
    </Dialog>
  );
});

export const IdentificationDocumentationView2 = observer(function IdentificationDocumentationView2({
  dispatch,
  item,
  attachmentType,
}: {
  dispatch: (event: DbotEvent) => void;
  item: DecisionListItem;
  attachmentType: AttachmentType;
}) {
  const { t } = useTranslation();
  const locale = useLanguage();
  const attachment = item.uploadedDecisionAttachments.find((a) => a.type === attachmentType);
  const followUp = item.followUps.find(
    (d) => d.data.type === FollowUpType.attachment && d.data.attachmentType === attachmentType,
  );
  const newMembers = useMemo(() => getMembers(item), [item]);
  const [editEnabled, setEditEnabled] = useState<boolean>(
    followUp !== undefined && followUp.isActive && !attachment,
  );

  const form = useMemo(
    () =>
      editEnabled && followUp
        ? new ProvideIdentificationToPersonsForm(
            dispatch,
            attachmentType,
            item.decision.decisionId,
            followUp,
            newMembers.items,
            locale,
          )
        : null,
    [
      attachmentType,
      followUp,
      dispatch,
      editEnabled,
      item.decision.decisionId,
      newMembers.items,
      locale,
    ],
  );

  useEffect(() => {
    if (attachment) {
      setEditEnabled(false);
    }
  }, [attachment]);

  return !followUp ? null : (
    <VBox gap={"md"}>
      <span>{t(newMembers.description)}</span>
      {form ? (
        <>
          {form.identifications.map((m, index) => (
            <VBox key={m.name} gap={"xs"}>
              <strong>{m.name}</strong>
              {m.showNationality && (
                <MSelect
                  label={{ key: "decisions.electionOfCompanyBoard.boardChange.dialog.nationality" }}
                  model={m.nationality}
                  onSelect={m.setNationality}
                />
              )}
              <MTextInput
                label={t("settings.socialSecurityNumber")}
                model={m.identityNumberModel}
                autofocus={index === 0}
              />
              {m.showDnr && (
                <MTextInput
                  label={{ key: "decisions.electionOfCompanyBoard.boardChange.dialog.dnr" }}
                  model={m.dnr}
                />
              )}
              {m.requiresEmail && <MTextInput label={t("email")} model={m.emailModel} />}
            </VBox>
          ))}
          <Button variant={"primary"} label={t("save")} onClick={form.validateAndSave} />
        </>
      ) : attachment ? (
        <>
          <Button
            variant={"primary"}
            icon={"edit"}
            label={t("edit")}
            onClick={() => setEditEnabled(true)}
          />
          <Button
            variant={"secondary"}
            icon={"trash"}
            label={t("delete")}
            onClick={() =>
              dispatch(deleteAttachmentEvent(attachment.id, followUp, item.decision.decisionId))
            }
          />
        </>
      ) : followUp.isActive ? (
        <Button variant={"primary"} label={t("add")} onClick={() => setEditEnabled(true)} />
      ) : (
        <em>{t("dbot.summary.not-ready-help-text")}</em>
      )}
    </VBox>
  );
});

export const RequiredDocumentationView = observer(function RequiredDocumentationView({
  dispatch,
  item,
  attachmentType,
  followUp,
}: {
  dispatch: DbotEventDispatcher;
  item: DecisionListItem;
  attachmentType: AttachmentType;
  followUp: FollowUp;
}) {
  const { t } = useTranslation();
  const messageStore = useMessageStore();
  const attachments = item.uploadedDecisionAttachments.filter((a) => a.type === attachmentType);

  const form = useMemo(
    () =>
      followUp && followUp.data.type === FollowUpType.attachment
        ? new ProvideEmailToAttachmentForm(
            dispatch,
            attachmentType,
            item.decision.decisionId,
            followUp.data,
          )
        : null,
    [attachmentType, followUp, dispatch, item.decision.decisionId],
  );
  return (
    <VBox gap={"md"}>
      {when(getDecisionAttachmentsDescription(attachmentType), t)}
      {!followUp.isActive ? (
        <em>{t("dbot.summary.not-ready-help-text")}</em>
      ) : (
        <>
          <AttachmentsPanel
            fileUploadConfig={item.fileUploadConfig}
            uploadedAttachments={attachments.map((attachment) => {
              return { id: attachment.id, filename: attachment.name };
            })}
            onOpen={(attachmentId) => dispatch({ type: "dbot/open-attachment", attachmentId })}
            onDelete={(attachmentId) =>
              dispatch({
                type: "dbot/delete-attachment",
                attachmentId,
                source: followUp,
                reference: item.decision.decisionId,
              })
            }
            onUpload={(files) =>
              item.uploadNewDecisionAttachment(files, dispatch, attachmentType, followUp)
            }
            onInvalidFile={messageStore.showError}
            title={false}
          />
          {form && form.data.requiresEmail && (
            <>
              <MTextInput
                label={when(getDecisionAttachmentsEmailTitle(attachmentType), t)}
                model={form.email}
              />
              <Alert
                type={"info"}
                message={<>{when(getDecisionAttachmentsEmailInformation(attachmentType), t)}</>}
              />
            </>
          )}
        </>
      )}
    </VBox>
  );
});

export const SendToBRView = observer(function SendToBRView({
  item,
  dispatch,
  context,
}: {
  item: DecisionListItem;
  dispatch: (event: DbotEvent) => void;
  context: ProjectContext;
}) {
  const { t } = useTranslation();
  const locale = useLanguage();
  const followUp = item.followUps.find((d) => d.data.type === FollowUpType.sendToBR);
  if (!followUp) return null;
  const data = followUp.data as SendToBRFollowUp;
  if (!item.isEnabled) {
    return (
      <>
        <p>{t("dbot.summary.disabledSendToBR-1")}</p>
        <p>
          <span>{t("dbot.summary.disabledSendToBR-2")}</span>{" "}
          <a href={adminSettings(context.orgNo)}>{t("settings")}</a>{" "}
          <span>{t("dbot.summary.disabledSendToBR-3")}</span>
        </p>
      </>
    );
  } else if (followUp.status !== FollowUpStatus.READY) {
    return (
      <VBox gap={"lg"}>
        {data.updatedDate !== undefined && (
          <Markdown
            value={t("dbot.summary.status.updateTime", {
              date: t(relativeTime(data.updatedDate, locale)),
            })}
          />
        )}
        {followUp.status === FollowUpStatus.COMPLETED && (
          <>
            <Alert
              type="success"
              message={t("dbot.summary.isApprovedInBR", {
                date: formatDate(locale, data.brPublishingDate),
              })}
            />
          </>
        )}
        {followUp.status === FollowUpStatus.IN_PROGRESS && (
          <p>
            {data.brStatus === "SENT_TO_BR" && t("dbot.summary.isSubmittedToBR")}
            {data.brStatus === "SENT_FOR_SIGNING" && t("dbot.summary.isSentForSigningForBR")}
            {data.brStatus === "PROCESSING" && t("dbot.summary.isProcessingInBR")}
            {data.brStatus === "BR_APPROVED" && t("dbot.summary.isSubmittedToBR")}
          </p>
        )}
      </VBox>
    );
  } else if (!followUp.isActive) {
    return (
      <>
        <p>{t(getDecisionSendToBRDescription(item.decision.type))}</p>
        <p>
          <em>{t("dbot.summary.not-ready-help-text")}</em>
        </p>
      </>
    );
  }
  return (
    <>
      <p>{t(getDecisionSendToBRDescription(item.decision.type))}</p>
      <Button
        variant={"primary"}
        label={t("send")}
        onClick={(e) => {
          if (e.shiftKey && !isProductionEnvironment()) {
            dispatch({
              type: "dbot/autoapprove-send-to-br",
              decisionId: item.decision.decisionId,
              label: { translated: "Autoapprove decision sent to BR" },
            });
          } else {
            dispatch(item.actions.confirmSendToBR);
          }
        }}
        loading={item.isSendingToBR}
      />
    </>
  );
});

export const ApplyToSystemView = observer(function ApplyToSystemView({
  item,
  dispatch,
  context,
}: {
  item: DecisionListItem;
  dispatch: DbotEventDispatcher;
  context: ProjectContext;
}) {
  const { t } = useTranslation();
  const followUp = item.followUps.find((d) => d.data.type === FollowUpType.apply);
  const brFollowUp = item.followUps.find((d) => d.data.type === FollowUpType.sendToBR);

  if (!followUp) return null;
  else if (!item.isEnabled) {
    return (
      <>
        <p>{t("dbot.summary.disabledApply-1")}</p>
        <p>
          <span>{t("dbot.summary.disabledApply-2")}</span>{" "}
          <a href={adminSettings(context.orgNo)}>{t("settings")}</a>{" "}
          <span>{t("dbot.summary.disabledApply-3")}</span>
        </p>
      </>
    );
  } else if (item.applied) {
    return (
      <>
        <p>
          <Icon name={"check"} color={"success"} /> {t("dbot.summary.appliedDecisionToSystem")}
        </p>
      </>
    );
  } else if (!followUp.isActive) {
    return (
      <>
        <p>{t("dbot.summary.applyDecisionToSystem.tooltip")}</p>
        <p>
          <em>{t("dbot.summary.not-ready-help-text")}</em>
        </p>
      </>
    );
  } else if (!item.isPreviousDecisionApplied) {
    return <p>{t("dbot.summary.disabledApplyPreviousDecision")}</p>;
  }
  return (
    <>
      <p>{t("dbot.summary.applyDecisionToSystem.tooltip")}</p>
      {followUp.data.type === "apply" &&
        brFollowUp?.status !== FollowUpStatus.COMPLETED &&
        followUp.data.isRightsFromSubscription && (
          <Alert
            type={"info"}
            message={
              <VBox>
                <Markdown value={t("dbot.summary.isRightsFromSubscription")} />
              </VBox>
            }
          />
        )}
      {followUp.data.type === "apply" &&
        brFollowUp?.status !== FollowUpStatus.COMPLETED &&
        !followUp.data.isRightsFromSubscription &&
        item.decision.type === DecisionType.increaseOfShareCapital && (
          <Alert
            type={"info"}
            message={
              <VBox>
                <Markdown value={t("dbot.summary.isShareCapitalIncreaseWithoutRights")} />
              </VBox>
            }
          />
        )}
      <Button
        variant={"primary"}
        label={t("register")}
        onClick={() => {
          if (
            followUp.data.type === "apply" &&
            brFollowUp?.status !== FollowUpStatus.COMPLETED &&
            item.decision.type === DecisionType.increaseOfShareCapital
          ) {
            dispatch(
              confirmAction({
                text: { key: "dbot.summary.shareCapitalIncrease.confirm" },
                confirmLabel: { key: "yes" },
                cancelLabel: { key: "no" },
                onConfirm: [thunk(() => dispatch(item.actions.applyDecision))],
              }),
            );
          } else dispatch(item.actions.applyDecision);
        }}
        loading={item.isApplying}
      />
    </>
  );
});

export const FollowUpDecisionDetailsPanel = observer(function FollowUpDecisionDetailsPanel({
  panel,
  item,
  dispatch,
  context,
}: {
  panel: FollowUpDetailsPanel;
  item: DecisionListItem;
  dispatch: DbotEventDispatcher;
  context: ProjectContext;
}) {
  const locale = useLanguage();
  const panels = getDecisionFollowUpPanels(item, locale);
  const panelData = panels.find((p) => {
    switch (panel.followUpType) {
      case FollowUpType.apply:
      case FollowUpType.sendToBR:
        return p.followUp?.item.data.type === panel.followUpType;
      case FollowUpType.attachment: {
        return p.followUp?.item.data.type === panel.followUpType && p.id === panel.attachmentType;
      }
    }
  });
  const followUp = panelData?.followUp?.item;

  function render() {
    switch (followUp?.data.type) {
      case FollowUpType.attachment: {
        switch (followUp.data.attachmentType) {
          case AttachmentType.BOARD_MEMBERS_ELECTION_IDENTIFICATIONS:
          case AttachmentType.PROCURA_BY_NAME_OTHERS_IDENTIFICATION:
          case AttachmentType.EMPLOY_CEO_IDENTIFICATION:
            return (
              <IdentificationDocumentationView2
                dispatch={dispatch}
                item={item}
                attachmentType={followUp.data.attachmentType}
              />
            );
          case AttachmentType.CONFIRMATION_SHARE_CAPITAL_CONTRIBUTIONS:
          case AttachmentType.CONFIRMATION_SHARE_CAPITAL_NON_CASH_CONTRIBUTIONS:
            return (
              <RequiredDocumentationView
                dispatch={dispatch}
                item={item}
                attachmentType={followUp.data.attachmentType}
                followUp={followUp}
              />
            );
          default:
            return null;
        }
      }
      case FollowUpType.sendToBR:
        return <SendToBRView item={item} dispatch={dispatch} context={context} />;
      case FollowUpType.apply:
        return <ApplyToSystemView item={item} dispatch={dispatch} context={context} />;
      default:
        return null;
    }
  }

  return (
    <EditDetailsPanel
      dispatch={dispatch}
      title={{ key: "dbot.follow-up" }}
      heading={panelData?.heading}
      content={render}
      next={getOpenNextPanelEvent(panelData?.id ?? "", panels)}
      prev={getOpenPrevPanelEvent(panelData?.id ?? "", panels)}
      toggleMaximized={{ type: "dbot/toggle-maximize-details-panel" }}
      isMaximized={context.detailsPanelMaximized}
    />
  );
});

export const FollowUpDecisionSummary = observer(function FollowUpDecisionSummary({
  item,
  companies,
  includeCompanyName,
}: {
  item: DecisionListItem;
  companies: ProjectCompany[];
  includeCompanyName?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <Box alignItems="center">
      <span className={"semibold"}>{t(getDecisionName(item.decision.type))}</span>
      {includeCompanyName && (
        <span className={"subtle"}>{companies.find((c) => c.orgNo === item.orgNo)?.name}</span>
      )}
    </Box>
  );
});

export const FollowUpDecision = observer(function FinishDecision({
  item,
  dispatch,
}: {
  item: DecisionListItem;
  dispatch: DbotEventDispatcher;
}) {
  const locale = useLanguage();
  const { t } = useTranslation();
  return (
    <VBox>
      <DetailsSummaryList
        dispatch={dispatch}
        isLocked={false}
        panels={getDecisionFollowUpPanels(item, locale)}
      />
      {!item.supportsSendToBR && (
        <small className={"subtle"}>{t("dbot.summary.sendToBR.notSupported")}</small>
      )}
    </VBox>
  );
});
